import { useEffect, useState } from 'react';
import { useAdminStore } from '../../stores/adminStore';
import { useStageStore } from '../../stores/stageStore';
import { useTarifConditionStore } from '../../stores/tarifConditionStore';
import { useWaitingListStore } from '../../stores/waitingListStore';
import { Plus, AlertTriangle, Loader2, List as ListWait, RefreshCw, Archive } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

import SessionFilters from './components/SessionFilters';
import SessionTable from './components/SessionTable';
import SessionFormModal from './components/SessionFormModal';
import BulkActionBar from './components/BulkActionBar';
import WaitingListModal from '../../components/admin/WaitingListModal';
import { CHEMINS } from '../../lib/navigation/chemins';

interface SessionFormData {
  stage_id: string;
  center_id: string;
  periode: 'Détente' | 'Printemps' | 'Été' | 'Automne' | 'Hiver';
  semaine?: string;
  start_date: string;
  end_date: string;
  nombre_jours?: number;
  capacity: number;
  prix_normal: number;
  prix_reduit?: number;
  prix_local?: number;
  prix_local_reduit?: number;
  remarques?: string;
  tarif_condition_id?: string;
  visible_from?: string;
}

const PERIODES = ['Détente', 'Printemps', 'Été', 'Automne', 'Hiver'] as const;

/*
  Cette liste est desormais un onglet de `AdminProgrammationPage`, qui porte
  l'entete de la page. `sansEntete` evite deux titres empiles — et garde la page
  utilisable seule si un lien direct y mene encore.
*/
interface AdminSessionsPageProps {
  sansEntete?: boolean;
}

const AdminSessionsPage = ({ sansEntete = false }: AdminSessionsPageProps) => {
  const {
    sessions, centers, isLoading, error,
    fetchSessions, fetchCenters,
    createSession, updateSession, deleteSession,
    archiveSession, unarchiveSession,
  } = useAdminStore();
  const { stages, fetchStages } = useStageStore();
  const { conditions, fetchConditions } = useTarifConditionStore();
  const { entries: waitingListEntries, fetchWaitingList } = useWaitingListStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<any>(null);
  const [formData, setFormData] = useState<SessionFormData>({
    stage_id: '',
    center_id: '',
    periode: 'Été',
    semaine: '',
    start_date: '',
    end_date: '',
    capacity: 0,
    prix_normal: 0,
    prix_reduit: 0
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Filter state
  const [selectedCenter, setSelectedCenter] = useState<string | null>(null);
  const [selectedPeriode, setSelectedPeriode] = useState<string | null>(null);
  const [selectedSemaine, setSelectedSemaine] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeletePending, setBulkDeletePending] = useState(false);

  // Waiting list modal state
  const [isWaitingListModalOpen, setIsWaitingListModalOpen] = useState(false);
  const [selectedActivityForWaitingList, setSelectedActivityForWaitingList] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions(showArchived);
    fetchStages();
    fetchCenters();
    fetchConditions();
    fetchWaitingList();
  }, []);

  useEffect(() => {
    fetchSessions(showArchived);
    setSelectedIds(new Set());
  }, [showArchived]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedCenter, selectedPeriode, selectedSemaine, selectedStage]);

  const handleDuplicate = (session: any) => {
    setEditingSession(null);
    setFormData({
      stage_id: session.stage_id,
      center_id: session.center_id,
      periode: session.periode,
      semaine: session.semaine,
      start_date: session.start_date,
      end_date: session.end_date,
      nombre_jours: session.nombre_jours,
      capacity: session.capacity,
      prix_normal: session.prix_normal,
      prix_reduit: session.prix_reduit || undefined,
      prix_local: session.prix_local || undefined,
      prix_local_reduit: session.prix_local_reduit || undefined,
      remarques: session.remarques || '',
      tarif_condition_id: session.tarif_condition_id || undefined,
      visible_from: session.visible_from
    });
    setIsModalOpen(true);
  };

  const handleOpenModal = (session?: any) => {
    if (session) {
      setEditingSession(session);
      setFormData({
        stage_id: session.stage_id,
        center_id: session.center_id,
        periode: session.periode,
        semaine: session.semaine,
        start_date: session.start_date,
        end_date: session.end_date,
        nombre_jours: session.nombre_jours,
        capacity: session.capacity,
        prix_normal: session.prix_normal,
        prix_reduit: session.prix_reduit,
        prix_local: session.prix_local,
        prix_local_reduit: session.prix_local_reduit,
        remarques: session.remarques,
        tarif_condition_id: session.tarif_condition_id,
        visible_from: session.visible_from
      });
    } else {
      setEditingSession(null);
      setFormData({
        stage_id: '',
        center_id: '',
        periode: 'Été',
        semaine: '',
        start_date: '',
        end_date: '',
        capacity: 0,
        prix_normal: 0,
        prix_reduit: 0
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const sessionData = {
        stage_id: formData.stage_id,
        center_id: formData.center_id,
        semaine: formData.semaine,
        periode: formData.periode,
        start_date: formData.start_date,
        end_date: formData.end_date,
        nombre_jours: formData.nombre_jours,
        capacity: parseInt(formData.capacity.toString()),
        prix_normal: parseFloat(formData.prix_normal.toString()),
        prix_reduit: formData.prix_reduit ? parseFloat(formData.prix_reduit.toString()) : null,
        prix_local: formData.prix_local ? parseFloat(formData.prix_local.toString()) : null,
        prix_local_reduit: formData.prix_local_reduit ? parseFloat(formData.prix_local_reduit.toString()) : null,
        remarques: formData.remarques,
        tarif_condition_id: formData.tarif_condition_id,
        visible_from: formData.visible_from,
        active: true
      };

      if (editingSession) {
        await updateSession(editingSession.id, sessionData);
      } else {
        await createSession(sessionData);
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving session:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSession(id);
      setDeleteId(null);
      toast.success('Session supprimée');
    } catch (error: any) {
      /*
        La base refuse desormais de supprimer une session portant des
        inscriptions, et sa phrase dit combien il y en a et quoi faire a la
        place. La ravaler dans la console laissait l'administration devant un
        bouton qui ne fait rien.
      */
      toast.error(error?.message ?? 'La session n’a pas pu être supprimée.', { duration: 8000 });
    }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveId) return;
    try {
      await archiveSession(archiveId);
      setArchiveId(null);
      toast.success('Session archivée');
    } catch (error: any) {
      console.error('Error archiving session:', error);
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await unarchiveSession(id);
      toast.success('Session désarchivée');
    } catch (error: any) {
      console.error('Error unarchiving session:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchSessions(showArchived);
      toast.success('Données actualisées');
    } catch (error) {
      toast.error("Erreur lors de l'actualisation");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenWaitingListModal = (activityId: string) => {
    setSelectedActivityForWaitingList(activityId);
    setIsWaitingListModalOpen(true);
  };

  // --- Selection handlers ---
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const visibleIds = filteredSessions.map(s => s.id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleIds));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // --- Bulk actions ---
  const handleBulkArchive = async () => {
    const toArchive = filteredSessions.filter(
      s => selectedIds.has(s.id) && !s.archived
    );
    if (toArchive.length === 0) {
      clearSelection();
      return;
    }
    try {
      await Promise.all(toArchive.map(s => archiveSession(s.id)));
      clearSelection();
      toast.success(`${toArchive.length} session${toArchive.length > 1 ? 's' : ''} archivée${toArchive.length > 1 ? 's' : ''}`);
    } catch (error: any) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const handleBulkDuplicate = async () => {
    const ids = Array.from(selectedIds);
    const count = ids.length;
    const sessionsToDuplicate = filteredSessions.filter(s => ids.includes(s.id));
    try {
      await Promise.all(
        sessionsToDuplicate.map(s =>
          createSession({
            stage_id: s.stage_id,
            center_id: s.center_id,
            periode: s.periode,
            semaine: s.semaine,
            start_date: s.start_date,
            end_date: s.end_date,
            nombre_jours: s.nombre_jours,
            capacity: s.capacity,
            prix_normal: s.prix_normal,
            prix_reduit: s.prix_reduit,
            prix_local: s.prix_local,
            prix_local_reduit: s.prix_local_reduit,
            remarques: s.remarques,
            tarif_condition_id: s.tarif_condition_id,
            visible_from: s.visible_from,
            active: true,
            archived: false,
          })
        )
      );
      clearSelection();
      toast.success(`${count} session${count > 1 ? 's' : ''} dupliquée${count > 1 ? 's' : ''}`);
    } catch (error: any) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = Array.from(selectedIds);
    /*
      En serie et non en parallele : la base refuse une session portant des
      inscriptions, et un `Promise.all` rendrait le premier refus en laissant
      les autres suppressions se faire — donc un lot a moitie applique dont
      personne ne saurait dire la moitie.
    */
    const supprimees: string[] = [];
    for (const id of ids) {
      try {
        await deleteSession(id);
        supprimees.push(id);
      } catch (error: any) {
        setBulkDeletePending(false);
        setSelectedIds(new Set(ids.filter(i => !supprimees.includes(i))));
        toast.error(
          `${supprimees.length} session(s) supprimée(s), puis arrêt : ${error?.message ?? 'refus de la base'}`,
          { duration: 10000 },
        );
        return;
      }
    }
    clearSelection();
    setBulkDeletePending(false);
    const count = supprimees.length;
    toast.success(`${count} session${count > 1 ? 's' : ''} supprimée${count > 1 ? 's' : ''}`);
  };

  /*
    Les journées pédagogiques sont des sessions, mais elles se programment et
    se modifient dans leur propre module (prix d'après la date limite, classes
    admises) : cette liste ne montre que les stages. Et un stage ne se
    programme que dans un centre de vacances (VALIDÉ ÉCLOSION, 20/10/2026).
  */
  const sessionsDeStages = sessions.filter(
    (s) => (s.stage as { type_activite?: string | null } | null | undefined)?.type_activite !== 'jpa',
  );
  const centresDeVacances = centers.filter((c) => c.centre_de_vacances !== false);

  // --- Filtered sessions ---
  const filteredSessions = sessionsDeStages.filter((s) =>
    (showArchived || !s.archived) &&
    (!selectedCenter || s.center_id === selectedCenter) &&
    (!selectedPeriode || s.periode === selectedPeriode) &&
    (!selectedSemaine || s.semaine === selectedSemaine) &&
    (!selectedStage || s.stage_id === selectedStage)
  );

  const waitingListCounts = waitingListEntries.reduce((acc, entry) => {
    if (!acc[entry.activity_id]) acc[entry.activity_id] = 0;
    if (entry.status === 'waiting' || entry.status === 'invited') acc[entry.activity_id]++;
    return acc;
  }, {} as Record<string, number>);

  const archivedCount = sessionsDeStages.filter(s => s.archived).length;

  if (isLoading && sessions.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          {!sansEntete && (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Programmation des stages</h1>
              <p className="text-gray-600 mt-1">Gérez les sessions de stages et leur planification</p>
            </>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn-outline flex items-center"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <Link to={CHEMINS.planification.listeAttente} className="btn-outline flex items-center">
            <ListWait className="h-5 w-5 mr-2" />
            Liste d'attente
          </Link>
          <button onClick={() => handleOpenModal()} className="btn-primary flex items-center">
            <Plus className="h-5 w-5 mr-2" />
            Nouvelle session
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      <SessionFilters
        selectedCenter={selectedCenter}
        selectedPeriode={selectedPeriode}
        selectedSemaine={selectedSemaine}
        selectedStage={selectedStage}
        onCenterChange={setSelectedCenter}
        onPeriodeChange={setSelectedPeriode}
        onSemaineChange={setSelectedSemaine}
        onStageChange={setSelectedStage}
        centers={centresDeVacances}
        stages={stages}
        periodes={PERIODES}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
        archivedCount={archivedCount}
      />

      <BulkActionBar
        selectedCount={selectedIds.size}
        onArchive={handleBulkArchive}
        onDuplicate={handleBulkDuplicate}
        onDelete={() => setBulkDeletePending(true)}
        onClearSelection={clearSelection}
      />

      <SessionTable
        sessions={filteredSessions}
        stages={stages}
        centers={centresDeVacances}
        handleOpenModal={handleOpenModal}
        handleDuplicate={handleDuplicate}
        setDeleteId={setDeleteId}
        waitingListCounts={waitingListCounts}
        onViewWaitingList={handleOpenWaitingListModal}
        onArchive={(id) => setArchiveId(id)}
        onUnarchive={handleUnarchive}
        showArchived={showArchived}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
      />

      <SessionFormModal
        isModalOpen={isModalOpen}
        editingSession={editingSession}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsModalOpen}
        stages={stages}
        centers={centresDeVacances}
        conditions={conditions}
      />

      {/* Bulk Delete Confirmation */}
      {bulkDeletePending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 rounded-full p-2 mr-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Supprimer {selectedIds.size} session{selectedIds.size > 1 ? 's' : ''} ?
              </h3>
            </div>
            <p className="text-gray-600 mb-4">
              Cette action est irréversible. Les {selectedIds.size} session{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''} seront définitivement supprimées.
            </p>
            {/*
              Le compte des inscriptions concernees, dit AVANT le clic. Le texte
              precedent ne les mentionnait pas — et `registrations.activity_id`
              est en ON DELETE CASCADE vers les notes de credit, les presences et
              les annulations. La base refuse maintenant, mais l'ecran doit dire
              pourquoi plutot que de laisser tenter.
            */}
            {(() => {
              const avecInscrits = filteredSessions.filter(
                s => selectedIds.has(s.id) && (s.current_registrations || 0) > 0
              );
              if (avecInscrits.length === 0) {
                return (
                  <p className="text-sm text-gray-500 mb-6">
                    Aucune de ces sessions ne porte d’inscription.
                  </p>
                );
              }
              return (
                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <strong>{avecInscrits.length}</strong> de ces sessions porte
                  {avecInscrits.length > 1 ? 'nt' : ''} des inscriptions. La base les
                  refusera : une session inscrite s’archive, elle ne se supprime pas.
                  Décochez-les, ou archivez-les à la place.
                </div>
              );
            })()}
            <div className="flex justify-end space-x-3">
              <button onClick={() => setBulkDeletePending(false)} className="btn-outline">
                Annuler
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive single Confirmation */}
      {archiveId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-amber-100 rounded-full p-2 mr-3">
                <Archive className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Archiver la session</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Cette session sera masquée de la liste principale. Vous pourrez la retrouver en activant "Voir les sessions archivées". Cette action est réversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setArchiveId(null)} className="btn-outline">Annuler</button>
              <button
                onClick={handleArchiveConfirm}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition-colors"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archiver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete single Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 rounded-full p-2 mr-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cette session ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setDeleteId(null)} className="btn-outline">Annuler</button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waiting List Modal */}
      {selectedActivityForWaitingList && (
        <WaitingListModal
          isOpen={isWaitingListModalOpen}
          onClose={() => setIsWaitingListModalOpen(false)}
          activityId={selectedActivityForWaitingList}
        />
      )}

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminSessionsPage;
